import { CloseIcon } from '@chakra-ui/icons'
import { Alert, AlertIcon, Box, IconButton, Text } from '@chakra-ui/react'
import { CervicalExam, LaborEntry, LaborEntryType, WithId } from '@hb/shared'
import { useCallback, useMemo, useState } from 'react'
import { useLaborCharting } from './context'

// §4.2: Prompt thresholds — constants (not admin-configurable per spec;
// only §4.3 time tracking thresholds are admin-configurable).
const VITALS_ACTIVE_MS = 60 * 60 * 1000 // 60 min in active phase
const VITALS_SECOND_STAGE_MS = 15 * 60 * 1000 // 15 min in second stage
const FHR_ACTIVE_MS = 15 * 60 * 1000 // Q15 standard in active phase
const FHR_SECOND_STAGE_MS = 5 * 60 * 1000 // 5 min in second stage
const CERVICAL_ACTIVE_MS = 4 * 60 * 60 * 1000 // 4 hrs in active phase

// ACNM 2014: active phase begins at 6 cm dilation; second stage at 10 cm.
const ACTIVE_PHASE_DILATION_CM = 6
const SECOND_STAGE_DILATION_CM = 10

// ACNM thresholds for duration alerts (approximate — for informational alert only).
// Active phase: alert at > 6 hrs nulliparous, > 4 hrs multiparous (use 6 hrs conservatively).
// Second stage: alert at > 3 hrs nulliparous, > 2 hrs multiparous (use 3 hrs conservatively).
const ACTIVE_PHASE_DURATION_ALERT_MS = 6 * 60 * 60 * 1000
const SECOND_STAGE_DURATION_ALERT_MS = 3 * 60 * 60 * 1000

type LaborStage = 'latent' | 'active' | 'second'

const getLaborStage = (entries: WithId<LaborEntry<LaborEntryType>>[]): LaborStage => {
  const cervicalEntries = entries
    .filter(e => e.type === LaborEntryType.CERVICAL_EXAM)
    .sort((a, b) => b.startedAt - a.startedAt)
  const latest = cervicalEntries[0]
  if (!latest) return 'latent'
  const dilation = (latest.data as CervicalExam).dilation ?? 0
  if (dilation >= SECOND_STAGE_DILATION_CM) return 'second'
  if (dilation >= ACTIVE_PHASE_DILATION_CM) return 'active'
  return 'latent'
}

const getLastEntryTime = (
  entries: WithId<LaborEntry<LaborEntryType>>[],
  type: LaborEntryType,
): number | null => {
  const matching = entries.filter(e => e.type === type)
  if (!matching.length) return null
  return Math.max(...matching.map(e => e.startedAt))
}

// ── Individual prompt banner ──────────────────────────────────

const PromptBanner = ({ message, onDismiss }: { message: string; onDismiss: () => void }) => (
  <Alert
    status="warning"
    borderRadius={0}
    flexShrink={0}
    py={2}
    px={3}
    alignItems="flex-start"
    gap={2}>
    <AlertIcon mt={0.5} flexShrink={0} boxSize={4} />
    <Text fontSize="sm" fontFamily="Hero-New" flex={1}>
      {message}
    </Text>
    {/* §4.2: Dismissible — non-blocking, never a hard stop */}
    <IconButton
      aria-label="Dismiss"
      icon={<CloseIcon boxSize={2.5} />}
      size="xs"
      variant="ghost"
      flexShrink={0}
      alignSelf="center"
      onClick={onDismiss}
    />
  </Alert>
)

// ── Main component ────────────────────────────────────────────

export const LaborDocumentationPrompts = () => {
  const { laborEntries } = useLaborCharting()
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())

  const dismiss = useCallback((key: string) => setDismissed(prev => new Set([...prev, key])), [])

  const laborStage = useMemo(() => getLaborStage(laborEntries), [laborEntries])
  const now = Date.now()

  const lastVitalsAt = useMemo(
    () => getLastEntryTime(laborEntries, LaborEntryType.VITALS),
    [laborEntries],
  )
  const lastFhrAt = useMemo(
    () => getLastEntryTime(laborEntries, LaborEntryType.FHR),
    [laborEntries],
  )
  const lastCervicalAt = useMemo(
    () => getLastEntryTime(laborEntries, LaborEntryType.CERVICAL_EXAM),
    [laborEntries],
  )

  const prompts: { key: string; message: string }[] = useMemo(() => {
    if (laborStage === 'latent') return []

    const result: { key: string; message: string }[] = []

    // §4.2: Vitals prompt
    const vitalsThreshold = laborStage === 'second' ? VITALS_SECOND_STAGE_MS : VITALS_ACTIVE_MS
    if (!lastVitalsAt || now - lastVitalsAt > vitalsThreshold) {
      const mins = Math.round(vitalsThreshold / 60_000)
      result.push({
        key: 'vitals',
        message: `Maternal vitals have not been documented in the past ${mins} minutes. Consider documenting before continuing.`,
      })
    }

    // §4.2: FHR prompt
    const fhrThreshold = laborStage === 'second' ? FHR_SECOND_STAGE_MS : FHR_ACTIVE_MS
    if (!lastFhrAt || now - lastFhrAt > fhrThreshold) {
      const mins = Math.round(fhrThreshold / 60_000)
      result.push({
        key: 'fhr',
        message: `Fetal heart rate has not been documented in the past ${mins} minutes. ${laborStage === 'second' ? 'Second stage standard is Q5.' : 'Active labor standard is Q15.'}`,
      })
    }

    // §4.2: Cervical exam prompt — active phase only
    if (laborStage === 'active') {
      if (!lastCervicalAt || now - lastCervicalAt > CERVICAL_ACTIVE_MS) {
        result.push({
          key: 'cervical',
          message:
            'More than 4 hours have passed since the last cervical exam was documented. Clinical judgment governs timing.',
        })
      }
    }

    // §4.2: ACNM stage duration alert
    const activePhaseCervical = laborEntries
      .filter(
        e =>
          e.type === LaborEntryType.CERVICAL_EXAM &&
          (e.data as CervicalExam).dilation >= ACTIVE_PHASE_DILATION_CM &&
          (e.data as CervicalExam).dilation < SECOND_STAGE_DILATION_CM,
      )
      .sort((a, b) => a.startedAt - b.startedAt)

    const secondStageCervical = laborEntries
      .filter(
        e =>
          e.type === LaborEntryType.CERVICAL_EXAM &&
          (e.data as CervicalExam).dilation >= SECOND_STAGE_DILATION_CM,
      )
      .sort((a, b) => a.startedAt - b.startedAt)

    if (laborStage === 'active' && activePhaseCervical.length > 0) {
      const activeStart = activePhaseCervical[0].startedAt
      const activeDuration = now - activeStart
      if (activeDuration > ACTIVE_PHASE_DURATION_ALERT_MS) {
        const hrs = Math.round((activeDuration / 3_600_000) * 10) / 10
        result.push({
          key: 'acnm-active',
          message: `Active phase duration is approximately ${hrs} hours. ACNM threshold reached. This is an informational alert — clinical judgment governs. Alert logged in chart.`,
        })
      }
    }

    if (laborStage === 'second' && secondStageCervical.length > 0) {
      const secondStart = secondStageCervical[0].startedAt
      const secondDuration = now - secondStart
      if (secondDuration > SECOND_STAGE_DURATION_ALERT_MS) {
        const hrs = Math.round((secondDuration / 3_600_000) * 10) / 10
        result.push({
          key: 'acnm-second',
          message: `Second stage duration is approximately ${hrs} hours. ACNM threshold reached. This is an informational alert — clinical judgment governs. Alert logged in chart.`,
        })
      }
    }

    return result
  }, [laborStage, lastVitalsAt, lastFhrAt, lastCervicalAt, laborEntries, now])

  const visiblePrompts = prompts.filter(p => !dismissed.has(p.key))

  if (visiblePrompts.length === 0) return null

  return (
    <Box flexShrink={0}>
      {visiblePrompts.map(p => (
        <PromptBanner key={p.key} message={p.message} onDismiss={() => dismiss(p.key)} />
      ))}
    </Box>
  )
}
